import {makeApiCallPost, makeApiCallWithoutCatch} from "../../../Api/makeApiCall";
import {makeApolloClientCall, makeApolloClientMutationCall} from "../../../Api/makeApolloApiCall";
import {todayChicago} from "../../Alerts/MissingData/filters";

export const LINK = 'link';
export const UNLINK = 'unlink';

export const NOT_LINKED = 'NOT_LINKED';
export const BATCH_IN_PROGRESS = 'BATCH_IN_PROGRESS';
export const JOB_START_FAILED = 'JOB_START_FAILED';

const ERROR_MESSAGES = {
    [BATCH_IN_PROGRESS]: 'The store pipeline is running; try again after it finishes',
    [JOB_START_FAILED]: 'The change may still have gone through; cancel to check the store and refresh',
};

export const linkageErrorMessage = (error) => ERROR_MESSAGES[error.code] ?? error.message;

export const UNLINK_SOURCE_STORE_MUTATION = `
mutation unlinkSourceStore($canonicalStoreId: String!, $sourceStoreId: String!) {
    unlinkSourceStore(canonicalStoreId: $canonicalStoreId, sourceStoreId: $sourceStoreId) {
        canonicalStoreId
        sourceStoreId
        version
        storeIds
    }
}`;

export const LINK_SOURCE_STORE_MUTATION = `
mutation linkSourceStore($canonicalStoreId: String!, $sourceStoreId: String!) {
    linkSourceStore(canonicalStoreId: $canonicalStoreId, sourceStoreId: $sourceStoreId) {
        canonicalStoreId
        sourceStoreId
        version
        storeIds
    }
}`;

export const SOURCE_RECORD_LINKAGE_QUERY = `
query sourceRecordLinkage($version: String, $source: StoreSource, $sourceStoreId: String) {
    storeLinkage(version: $version, source: $source, sourceStoreId: $sourceStoreId) {
        canonicalStoreId
    }
}`;

export const CANONICAL_STORE_SEARCH_QUERY = `
query canonicalStoreSearch($version: String!, $query: String!, $excludeCanonicalStoreId: String) {
    canonicalStoreSearch(version: $version, query: $query, excludeCanonicalStoreId: $excludeCanonicalStoreId) {
        canonicalStoreId
        bannerName
        address
        city
        state
        postalCode
    }
}`;

export const STORE_LINKAGE_SALES_HISTORY_QUERY = `
query storeLinkageSalesHistory($version: String, $canonicalStoreId: String!) {
    storeUniverse(version: $version, canonicalStoreIdFilter: [$canonicalStoreId]) {
        items {
            id
            firstSalesDate
            salesHistoryCount
            latestSalesHistory: salesHistory(last: 1) {
                annualizedScv
                estimatedAcv
                scv
                date
            }
        }
    }
}`;

const toSalesHistorySummary = ({firstSalesDate, salesHistoryCount, latestSalesHistory}) => {
    const latestRecord = latestSalesHistory?.[0];
    return {
        weeksOfSalesHistory: salesHistoryCount ?? 0,
        firstSalesDate,
        latestWeek: latestRecord?.date,
        annualizedScv: latestRecord?.annualizedScv,
        estimatedAcv: latestRecord?.estimatedAcv,
        scv: latestRecord?.scv,
    };
};

export const salesHistoryForStore = ({version, canonicalStoreId}) =>
    makeApolloClientCall(STORE_LINKAGE_SALES_HISTORY_QUERY, false, {version, canonicalStoreId})
        .then(response => new Map((response?.storeUniverse?.items ?? [])
            .map(item => [item.id, toSalesHistorySummary(item)])));

export const searchCanonicalStores = ({version, query, excludeCanonicalStoreId}) =>
    makeApolloClientCall(CANONICAL_STORE_SEARCH_QUERY, false, {version, query, excludeCanonicalStoreId})
        .then(({canonicalStoreSearch}) => canonicalStoreSearch);

export const storeLinkageUrl = (linkageType, version, singleRowAction) =>
    `${process.env.REACT_APP_EDP_API_BASE_URL}/projectionsStoreLinkage/${linkageType}/?version=${encodeURIComponent(version)}&single_row_action=${singleRowAction}`;

export const getLinkages = (linkageType, version) =>
    makeApiCallWithoutCatch(`${process.env.REACT_APP_EDP_API_BASE_URL}/projectionsStoreLinkage/${linkageType}/?version=${encodeURIComponent(version)}`)
        .then(response => response?.data ?? []);

export const manualLinksForStore = ({version, canonicalStoreId}) =>
    getLinkages(LINK, version).then(rows => rows
        .filter(row => `${version}::${row.canonicalStoreId}` === canonicalStoreId)
        .reduce((manualLinks, row) => {
            const memberId = `${row.source}::${version}::${row.sourceStoreId}`;
            const existing = manualLinks.get(memberId);
            if (!existing || row.index > existing.index) manualLinks.set(memberId, row);
            return manualLinks;
        }, new Map()));

export const saveLinkage = async (linkageType, {version, canonicalStoreId, sourceStoreId, notes, user, index}) => {
    const response = await makeApiCallPost(storeLinkageUrl(linkageType, version, 'update'), {
        canonicalStoreId,
        sourceStoreId,
        user,
        updateTime: todayChicago().format(),
        notes,
        ...(index !== undefined && {index}),
    });
    if (response?.apiError) throw response.apiError;
    return response;
};

export const removeLinkage = async (linkageType, {version, record}) => {
    const response = await makeApiCallPost(storeLinkageUrl(linkageType, version, 'delete'), record);
    if (response?.apiError) throw response.apiError;
    return response;
};

const treatAsApplied = (appliedCode) => (error) => {
    if (error.code === appliedCode) return {alreadyApplied: true};
    throw error;
};

export const storeLinkageCleanupUrl = (linkageType, version) =>
    `${process.env.REACT_APP_EDP_API_BASE_URL}/projectionsStoreLinkage/${linkageType}/cleanup/?version=${encodeURIComponent(version)}`;

export const cleanupLinkage = async (linkageType, {version, sourceStoreId, canonicalStoreId, keepIndex}) => {
    const response = await makeApiCallPost(storeLinkageCleanupUrl(linkageType, version), {sourceStoreId, canonicalStoreId, keepIndex});
    if (response?.apiError) throw response.apiError;
    return response;
};

export const runUnlink = ({canonicalStoreId, sourceStoreId}) =>
    makeApolloClientMutationCall(UNLINK_SOURCE_STORE_MUTATION, {canonicalStoreId, sourceStoreId}, {includeErrorCode: true})
        .then(response => response.unlinkSourceStore, treatAsApplied(NOT_LINKED));

const markAlreadyLinked = (result) => result.storeIds.length === 0 ? {...result, alreadyApplied: true} : result;

export const runLink = ({canonicalStoreId, sourceStoreId}) =>
    makeApolloClientMutationCall(LINK_SOURCE_STORE_MUTATION, {canonicalStoreId, sourceStoreId}, {includeErrorCode: true})
        .then(response => markAlreadyLinked(response.linkSourceStore));

export const currentCanonicalStoreId = ({version, source, sourceStoreId}) =>
    makeApolloClientCall(SOURCE_RECORD_LINKAGE_QUERY, false, {version, source, sourceStoreId})
        .then(({storeLinkage}) => {
            if (!storeLinkage) throw new Error(`No store linkage was found for ${source} ${sourceStoreId}`);
            return storeLinkage.canonicalStoreId;
        });
